import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Pencil, Trash2, Link2, Plus, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { v2Api } from '@/lib/api';
import type { ExerciseTeamMapping } from '@/lib/api/v2';

interface ExerciseTeamMappingsCardProps {
  exerciseId: string;
  initialMappings?: ExerciseTeamMapping[];
  onRefetch?: () => Promise<unknown>;
  locked?: boolean;
}

export function ExerciseTeamMappingsCard({
  exerciseId,
  initialMappings,
  onRefetch,
  locked = false,
}: ExerciseTeamMappingsCardProps) {
  const [roles, setRoles] = useState<Array<{ id: number; name: string }>>([]);
  const [mappings, setMappings] = useState<ExerciseTeamMapping[]>(initialMappings ?? []);
  const [newTeamId, setNewTeamId] = useState<number>(0);
  const [newVmRequired, setNewVmRequired] = useState(false);
  const [loading, setLoading] = useState(initialMappings === undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<ExerciseTeamMapping | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [mappingToEdit, setMappingToEdit] = useState<ExerciseTeamMapping | null>(null);
  const [editVmRequired, setEditVmRequired] = useState(false);
  const [editIsActive, setEditIsActive] = useState(true);
  const [editSaving, setEditSaving] = useState(false);

  const mappedTeamIds = useMemo(() => new Set(mappings.map((m) => m.team)), [mappings]);
  const availableRoles = useMemo(
    () => roles.filter((r) => !mappedTeamIds.has(r.id)),
    [roles, mappedTeamIds],
  );

  useEffect(() => {
    if (initialMappings !== undefined) setMappings(initialMappings);
  }, [initialMappings]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(initialMappings === undefined);
      setError(null);
      try {
        const roleList = await v2Api.dashboard.roles.list();
        const teamMappings =
          initialMappings !== undefined
            ? initialMappings
            : await v2Api.dashboard.exerciseTeamMappings.listByExerciseId(exerciseId);
        if (!mounted) return;
        setRoles((roleList ?? []).map((r) => ({ id: r.id, name: r.name })));
        if (initialMappings === undefined) setMappings(teamMappings);
        setNewTeamId(0);
        setNewVmRequired(false);
      } catch (err: unknown) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load exercise team mappings');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [exerciseId, initialMappings]);

  // Keep selection valid (must be an unmapped team).
  useEffect(() => {
    if (loading) return;
    if (availableRoles.length === 0) {
      if (newTeamId !== 0) setNewTeamId(0);
      return;
    }
    if (!availableRoles.some((r) => r.id === newTeamId)) {
      setNewTeamId(availableRoles[0]?.id ?? 0);
    }
  }, [availableRoles, newTeamId, loading]);

  const load = async () => {
    const [roleList, teamMappings] = await Promise.all([
      v2Api.dashboard.roles.list(),
      v2Api.dashboard.exerciseTeamMappings.listByExerciseId(exerciseId),
    ]);
    setRoles((roleList ?? []).map((r) => ({ id: r.id, name: r.name })));
    setMappings(teamMappings);
    setNewTeamId(0);
    setNewVmRequired(false);
  };

  const handleAdd = async () => {
    if (locked) return;
    if (!newTeamId) {
      setError('Please select a team.');
      return;
    }
    if (!availableRoles.some((r) => r.id === newTeamId)) {
      setError('This team is already mapped to the exercise.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await v2Api.dashboard.exerciseTeamMappings.create(exerciseId, {
        team: newTeamId,
        vm_required: newVmRequired,
        is_active: true,
      });
      if (onRefetch) {
        await onRefetch();
      } else {
        await load();
      }
      setNewTeamId(0);
      setNewVmRequired(false);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { errors?: Record<string, string[] | string>; detail?: string } } })?.response?.data;
      const teamErr = data?.errors?.team;
      const teamMessage = Array.isArray(teamErr) ? teamErr[0] : teamErr;
      setError(typeof teamMessage === 'string' ? teamMessage : (data?.detail ?? 'Failed to add mapping'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (locked) return;
    setSaving(true);
    setError(null);
    try {
      await v2Api.dashboard.exerciseTeamMappings.delete(exerciseId, id);
      if (onRefetch) {
        await onRefetch();
      } else {
        await load();
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err instanceof Error ? err.message : 'Failed to delete mapping');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-lg inline-flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Team Mappings
        </CardTitle>
        <CardDescription>
          Map teams and mark which ones require VM access. TTX remains available by default.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-destructive mb-3">{error}</p>}
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2">
          <Select
            value={newTeamId ? String(newTeamId) : ''}
            onChange={(e) => setNewTeamId(parseInt(e.target.value, 10) || 0)}
            disabled={locked || saving || availableRoles.length === 0}
          >
            <option value="">Select team</option>
            {availableRoles.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={locked || saving || !newTeamId || availableRoles.length === 0}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add
          </Button>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border"
              checked={newVmRequired}
              onChange={(e) => setNewVmRequired(e.target.checked)}
              disabled={locked || saving || !newTeamId}
            />
            VM required for this team
          </label>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground py-2">Loading mappings...</p>
        ) : mappings.length === 0 ? (
          <div className="flex items-start gap-2 text-sm text-muted-foreground py-2">
            <AlertCircle className="h-4 w-4 mt-0.5" />
            <p>
              No team mappings yet. Select a team above and click <span className="font-medium text-foreground">Add</span>.
            </p>
          </div>
        ) : availableRoles.length === 0 ? (
          <div className="flex items-start gap-2 text-sm text-muted-foreground py-1">
            <AlertCircle className="h-4 w-4 mt-0.5" />
            <p>All available teams are already mapped.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {mappings.map((m) => (
              <li key={m.id} className="flex items-start justify-between rounded border bg-muted/30 px-3 py-2 gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{m.team_name}</span>
                    <Badge variant={m.vm_required ? 'default' : 'secondary'}>
                      {m.vm_required ? 'VM required' : 'TTX default'}
                    </Badge>
                    <Badge variant={m.is_active ? 'default' : 'secondary'}>
                      {m.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Code: <span className="font-medium text-foreground">{m.team_code}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      if (locked) return;
                      setMappingToEdit(m);
                      setEditVmRequired(Boolean(m.vm_required));
                      setEditIsActive(Boolean(m.is_active));
                      setEditDialogOpen(true);
                    }}
                    disabled={locked || saving || editSaving}
                    aria-label={`Edit mapping for ${m.team_name}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => {
                      if (locked) return;
                      setMappingToDelete(m);
                      setDeleteDialogOpen(true);
                    }}
                    disabled={locked || saving}
                    aria-label={`Delete mapping for ${m.team_name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => !open && setDeleteDialogOpen(false)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete exercise-team mapping?</DialogTitle>
            <DialogDescription>
              This will remove the mapping. Walkthroughs that depend on it may no longer be editable.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={locked || saving}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={async () => {
                if (locked) return;
                if (!mappingToDelete) return;
                await handleDelete(mappingToDelete.id);
                setDeleteDialogOpen(false);
                setMappingToDelete(null);
              }}
              disabled={locked || saving || !mappingToDelete}
            >
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          if (!open) setEditDialogOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Edit exercise-team mapping</DialogTitle>
            <DialogDescription>
              Update how this exercise treats this team for VM/console access, and whether the mapping is active.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {mappingToEdit ? (
              <div className="rounded-lg border bg-muted/30 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{mappingToEdit.team_name}</p>
                    <p className="text-xs text-muted-foreground">
                      Code: <span className="font-medium text-foreground">{mappingToEdit.team_code}</span>
                    </p>
                  </div>
                  <Badge variant={mappingToEdit.vm_required ? 'default' : 'secondary'}>
                    {mappingToEdit.vm_required ? 'VM required' : 'TTX default'}
                  </Badge>
                </div>
              </div>
            ) : null}

            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border"
                checked={editVmRequired}
                onChange={(e) => setEditVmRequired(e.target.checked)}
                disabled={locked || editSaving}
              />
              VM required for this team
            </label>

            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border"
                checked={editIsActive}
                onChange={(e) => setEditIsActive(e.target.checked)}
                disabled={locked || editSaving}
              />
              Mapping is active
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              disabled={locked || editSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (locked) return;
                if (!mappingToEdit) return;
                setEditSaving(true);
                setError(null);
                try {
                  await v2Api.dashboard.exerciseTeamMappings.update(exerciseId, mappingToEdit.id, {
                    vm_required: editVmRequired,
                    is_active: editIsActive,
                  });
                  if (onRefetch) await onRefetch();
                  else await load();
                  setEditDialogOpen(false);
                } catch (err: unknown) {
                  const message =
                    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
                    (err instanceof Error ? err.message : 'Failed to update mapping');
                  setError(message);
                } finally {
                  setEditSaving(false);
                }
              }}
              disabled={locked || !mappingToEdit || editSaving}
            >
              {editSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
